import { Button } from '@/components/atoms';
import { BackArrowIcon } from '@/components/atoms/BackArrowIcon';
import { Typography } from '@/components/atoms/Typography';
import {
  BedIcon,
  CircleAlertIcon,
  DirhamIcon,
  ExpandIcon,
  InboxIcon,
  MailWithCheckIcon,
  ShowerHeadIcon,
} from '@/components/atoms/icons';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent, trackConfirmReservationDialogView } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useWebViewport } from '@/core/providers/WebViewportProvider';
import { useAppStore } from '@/core/stores/appStore';
import { hasNumericValue, hasTextValue } from '@/core/utils/fieldAvailability';
import { BREAKPOINTS, getBreakpointWidth } from '@/core/utils/responsive';
import { VIEWPORT_ZOOM_EPSILON } from '@/core/utils/viewportMetrics';
import React from 'react';
import {
  Image,
  type ImageSourcePropType,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import { normalizeAedAmount } from '../services/mapUnitDetails';
import type { UnitDetails } from '../types/property.types';
import { resolveProfileIskanLoanAmountAed } from '../utils/iskanLoan';


/** Large-screen unit-card thumbnail — Figma 107×75, radius Spacing/1 (4) */
const DESKTOP_THUMB_WIDTH = 107;
const DESKTOP_THUMB_HEIGHT = 75;
/** Mobile unit-card thumbnail — Figma 107×75, radius Spacing/1 (4) */
const MOBILE_THUMB_WIDTH = 107;
const MOBILE_THUMB_HEIGHT = 75;
const STEP_ICON_SIZE = 32;
/** Keep dialog inside the visible viewport (e.g. with DevTools open). */
const VIEWPORT_VERTICAL_INSET = 32;

/**
 * UI match — flip to show one Figma case at a time.
 *
 * Case A (`false`): Full Iskan cover
 *   Total Unit Price + Iskan loan (equal amounts). No remaining / note.
 *
 * Case B (`true`): Shortfall
 *   Total + Iskan (less) + Remaining Balance (red) + Important to Note.
 *
 * Set to `null` to derive from unit.preApprovalAmount.
 */
const SHOW_SHORTFALL_CASE: boolean | null = null;

/** Figma shortfall demo ratio when unit has no real loan shortfall */
const PREVIEW_ISKAN_RATIO = 0.8;

interface ReservationAmounts {
  hasShortfall: boolean;
  loanAmount: number;
  remainingBalance: number;
}

function getLoanFromUnit(unit: UnitDetails): number {
  if (
    typeof unit.preApprovalAmount === 'number' &&
    Number.isFinite(unit.preApprovalAmount)
  ) {
    return unit.preApprovalAmount;
  }
  return 0;
}

function resolveReservationAmounts(
  unit: UnitDetails,
  iskanLoanAmount?: number,
): ReservationAmounts {
  const totalPrice = normalizeAedAmount(unit.totalPrice);
  const loanFromUnit =
    typeof iskanLoanAmount === 'number' && Number.isFinite(iskanLoanAmount)
      ? normalizeAedAmount(iskanLoanAmount)
      : normalizeAedAmount(getLoanFromUnit(unit));
  const remainingFromUnit = Math.max(0, totalPrice - loanFromUnit);
  /** Shortfall whenever approved loan is below unit price (including loan = 0). */
  const derivedShortfall = totalPrice > 0 && loanFromUnit < totalPrice;
  const hasShortfall = SHOW_SHORTFALL_CASE ?? derivedShortfall;

  if (SHOW_SHORTFALL_CASE === false) {
    return {
      hasShortfall,
      loanAmount: totalPrice,
      remainingBalance: 0,
    };
  }

  if (SHOW_SHORTFALL_CASE === true) {
    if (loanFromUnit > 0 && remainingFromUnit > 0) {
      return {
        hasShortfall,
        loanAmount: loanFromUnit,
        remainingBalance: remainingFromUnit,
      };
    }
    const loanAmount = Math.round(totalPrice * PREVIEW_ISKAN_RATIO);
    return {
      hasShortfall,
      loanAmount,
      remainingBalance: Math.max(0, totalPrice - loanAmount),
    };
  }

  return {
    hasShortfall,
    loanAmount: loanFromUnit,
    remainingBalance: hasShortfall ? remainingFromUnit : 0,
  };
}


type ConfirmColors = ReturnType<typeof useUIColors>;
type ConfirmLabels = UnitDetailsLabels;

function ConfirmUnitSpecs({
  unit,
  isMobile,
  iconColor,
}: Readonly<{
  unit: UnitDetails;
  isMobile: boolean;
  iconColor: string;
}>) {
  const { listing: LABELS } = usePropertyLabels();

  return (
    <View style={isMobile ? styles.specsRowMobile : styles.specsRow}>
      {hasNumericValue(unit.bedrooms) ? (
        <SpecItem
          icon={<BedIcon size={14} color={iconColor} />}
          value={String(unit.bedrooms)}
          label={LABELS.bedrooms}
          compact={isMobile}
        />
      ) : null}
      {hasNumericValue(unit.bathrooms) ? (
        <SpecItem
          icon={<ShowerHeadIcon size={14} color={iconColor} />}
          value={String(unit.bathrooms)}
          label={LABELS.bathrooms}
          compact={isMobile}
        />
      ) : null}
      {hasNumericValue(unit.areaSqFt) ? (
        <SpecItem
          icon={<ExpandIcon size={14} color={iconColor} />}
          value={unit.areaSqFt.toLocaleString()}
          label={LABELS.sqft}
          compact={isMobile}
        />
      ) : null}
    </View>
  );
}

function ConfirmUnitCard({
  unit,
  unitTitle,
  imageSource,
  isMobile,
  colors,
  labels,
}: Readonly<{
  unit: UnitDetails;
  unitTitle: string;
  imageSource?: ImageSourcePropType | undefined;
  isMobile: boolean;
  colors: ConfirmColors;
  labels: ConfirmLabels;
}>) {
  const { isRTL } = useI18nContext();
  return (
    <View
      style={[
        isMobile ? styles.unitCardMobile : styles.unitCardDesktop,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
    >
      <View style={isMobile ? styles.unitTopMobile : styles.unitTop}>
        <View
          style={[
            isMobile ? styles.thumbMobile : styles.thumb,
            { backgroundColor: colors.backgroundLevel2 },
          ]}
        >
          {imageSource ? (
            <Image
              source={imageSource}
              style={styles.thumbImage}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          ) : null}
        </View>
        <View style={isMobile ? styles.unitMetaMobile : styles.unitMeta}>
          <Typography
            variant="headline"
            color="primary"
            style={[styles.unitTitle, { textAlign: isRTL ? 'right' : 'left' }]}
            numberOfLines={2}
          >
            {unitTitle}
          </Typography>
          <ConfirmUnitSpecs
            unit={unit}
            isMobile={isMobile}
            iconColor={colors.fontPrimary}
          />
        </View>
      </View>
      <View
        style={[
          styles.priceBar,
          { backgroundColor: colors.backgroundLevel2 },
        ]}
      >
        {hasNumericValue(unit.totalPrice) ? (
          <View style={styles.amountRow}>
            <DirhamIcon size={16} color={colors.fontPrimary} />
            <Typography
              variant="headlineLarge"
              color="primary"
              style={styles.priceBarAmount}
            >
              {unit.totalPrice.toLocaleString()}
            </Typography>
          </View>
        ) : null}
        {hasTextValue(unit.handoverDate) ? (
          <Typography
            variant="subhead"
            color="fontMuted"
            style={styles.handoverText}
          >
            {labels.HANDOVER_PREFIX} {unit.handoverDate}
          </Typography>
        ) : null}
      </View>
    </View>
  );
}

function ConfirmBreakdownCard({
  unit,
  isMobile,
  colors,
  labels,
  loanAmount,
  hasShortfall,
  remainingBalance,
}: Readonly<{
  unit: UnitDetails;
  isMobile: boolean;
  colors: ConfirmColors;
  labels: ConfirmLabels;
  loanAmount: number;
  hasShortfall: boolean;
  remainingBalance: number;
}>) {
  const desktopShortfallStyle =
    hasShortfall && !isMobile ? styles.breakdownCardDesktopShortfall : null;
  return (
    <View
      style={[
        isMobile ? styles.breakdownCardMobile : styles.breakdownCardDesktop,
        { backgroundColor: colors.backgroundLevel1 },
        desktopShortfallStyle,
      ]}
    >
      <BreakdownRow
        label={labels.TOTAL_UNIT_PRICE}
        amount={unit.totalPrice}
        color={colors.fontPrimary}
        hug={isMobile}
      />
      <DashedDivider color={colors.outlineDisabled} />
      <BreakdownRow
        label={labels.ISKAN_LOAN}
        amount={loanAmount}
        color={colors.fontPrimary}
        hug={isMobile}
      />
      {hasShortfall ? (
        <>
          <DashedDivider color={colors.outlineDisabled} />
          <BreakdownRow
            label={labels.REMAINING_BALANCE}
            amount={remainingBalance}
            color={colors.remainingBalanceText}
            hug={isMobile}
            leading={
              <CircleAlertIcon
                size={16}
                color={colors.remainingBalanceText}
              />
            }
          />
        </>
      ) : null}
    </View>
  );
}

function ConfirmImportantNote({
  isMobile,
  colors,
  labels,
}: Readonly<{
  isMobile: boolean;
  colors: ConfirmColors;
  labels: ConfirmLabels;
}>) {
  return (
    <View
      style={[
        isMobile ? styles.noteBoxMobile : styles.noteBoxDesktop,
        {
          backgroundColor: colors.importantNoteBg,
          borderColor: colors.importantNoteBorder,
        },
      ]}
    >
      <CircleAlertIcon size={18} color={colors.importantNoteTitle} />
      <View style={styles.noteText}>
        <Typography
          variant="headline"
          color={colors.importantNoteTitle}
          style={styles.noteTitle}
        >
          {labels.IMPORTANT_TO_NOTE}
        </Typography>
        <Typography
          variant="footnote"
          color={colors.importantNoteBody}
          style={styles.noteBody}
        >
          {labels.IMPORTANT_NOTE_BODY}
        </Typography>
      </View>
    </View>
  );
}

function ConfirmNextSteps({
  isMobile,
  colors,
  labels,
}: Readonly<{
  isMobile: boolean;
  colors: ConfirmColors;
  labels: ConfirmLabels;
}>) {
  return (
    <View
      style={[
        isMobile ? styles.nextBoxMobile : styles.nextBoxDesktop,
        {
          backgroundColor: colors.backgroundLevel2,
          borderColor: colors.confirmNextBorder,
        },
      ]}
    >
      <Typography variant="body" color="primary" style={styles.nextTitle}>
        {labels.WHAT_HAPPENS_NEXT}
      </Typography>
      <View style={isMobile ? styles.timelineMobile : styles.timelineDesktop}>
        <View
          style={[
            styles.stepConnector,
            { backgroundColor: colors.outlineDisabled },
          ]}
          pointerEvents="none"
        />
        <View style={styles.timelineStep}>
          <View
            style={[
              styles.stepIconCircle,
              {
                backgroundColor: colors.backgroundLevel1,
                borderColor: colors.outlineDisabled,
              },
            ]}
          >
            <MailWithCheckIcon size={16} color={colors.fontPrimary} />
          </View>
          <Typography
            variant="footnote"
            color="tertiary"
            style={styles.stepText}
          >
            {labels.NEXT_STEP_EMAIL}
          </Typography>
        </View>
        <View style={styles.timelineStep}>
          <View
            style={[
              styles.stepIconCircle,
              {
                backgroundColor: colors.backgroundLevel1,
                borderColor: colors.outlineDisabled,
              },
            ]}
          >
            <InboxIcon size={16} color={colors.fontPrimary} />
          </View>
          <Typography
            variant="footnote"
            color="tertiary"
            style={styles.stepText}
          >
            {labels.NEXT_STEP_DEADLINE}
          </Typography>
        </View>
      </View>
    </View>
  );
}

function ConfirmReservationScrollBody({
  unit,
  unitTitle,
  imageSource,
  isMobile,
  colors,
  labels,
  loanAmount,
  hasShortfall,
  remainingBalance,
}: Readonly<{
  unit: UnitDetails;
  unitTitle: string;
  imageSource?: ImageSourcePropType | undefined;
  isMobile: boolean;
  colors: ConfirmColors;
  labels: ConfirmLabels;
  loanAmount: number;
  hasShortfall: boolean;
  remainingBalance: number;
}>) {
  return (
    <>
      <View style={styles.unitAndBreakdown}>
        <ConfirmUnitCard
          unit={unit}
          unitTitle={unitTitle}
          imageSource={imageSource}
          isMobile={isMobile}
          colors={colors}
          labels={labels}
        />
        <ConfirmBreakdownCard
          unit={unit}
          isMobile={isMobile}
          colors={colors}
          labels={labels}
          loanAmount={loanAmount}
          hasShortfall={hasShortfall}
          remainingBalance={remainingBalance}
        />
      </View>
      {hasShortfall ? (
        <ConfirmImportantNote
          isMobile={isMobile}
          colors={colors}
          labels={labels}
        />
      ) : null}
      {/* What happens next — temporarily hidden; set true to restore */}
      {false ? (
        <ConfirmNextSteps isMobile={isMobile} colors={colors} labels={labels} />
      ) : null}
    </>
  );
}

export interface ConfirmReservationDialogProps {
  visible: boolean;
  unit: UnitDetails;
  /** Profile-details Iskan loan (AED). Missing → 0; drives shortfall UI. */
  iskanLoanAmount?: number;
  /** First gallery image when available */
  imageSource?: ImageSourcePropType | undefined;
  onCancel: () => void;
  onProceed: () => void;
}

/**
 * Confirm reservation modal (web unit details).
 * Two cases: full Iskan cover vs remaining-balance + important note.
 */
export function ConfirmReservationDialog({
  visible,
  unit,
  iskanLoanAmount,
  imageSource,
  onCancel,
  onProceed,
}: Readonly<ConfirmReservationDialogProps>) {
  React.useEffect(() => {
    if (visible) {
      trackConfirmReservationDialogView(unit.id);
    }
  }, [visible, unit.id]);

  const handleDismiss = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.CONFIRM_DIALOG_CANCEL,
      eventType: 'action',
    });
    onCancel();
  };

  const handleProceed = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.CONFIRM_DIALOG_CONFIRM,
      eventType: 'action',
    });
    onProceed();
  };

  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  // Use breakpointHeight (zoom-corrected) so the dialog cap isn't shrunk to 80%
  // of the viewport at 125% browser zoom, which would make the dialog too short.
  const { breakpointHeight: windowHeight, browserZoom } = useWebViewport();
  /**
   * Full-screen mobile when width < SHELL — same as before for phones/tablets.
   * Only exception: a true desktop viewport that dipped under SHELL solely because
   * of 125–<175% browser zoom (inferred 100% width still ≥ SHELL). Those keep the
   * desktop dialog until 175%. Naturally small screens never hit that exception.
   */
  const breakpointWidth = getBreakpointWidth();
  const widthAt100Percent =
    browserZoom > 1 + VIEWPORT_ZOOM_EPSILON
      ? breakpointWidth * browserZoom
      : breakpointWidth;
  const isZoomShrunkDesktop =
    breakpointWidth < BREAKPOINTS.SHELL &&
    widthAt100Percent >= BREAKPOINTS.SHELL &&
    browserZoom >= 1.25 - VIEWPORT_ZOOM_EPSILON &&
    browserZoom < 1.75 - VIEWPORT_ZOOM_EPSILON;
  const isMobile =
    breakpointWidth < BREAKPOINTS.SHELL && !isZoomShrunkDesktop;
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const profileLoanFromStore = useAppStore((s) => s.auth.user?.loan_Amount);
  const resolvedIskanLoan = React.useMemo(
    () => resolveProfileIskanLoanAmountAed(iskanLoanAmount, profileLoanFromStore),
    [iskanLoanAmount, profileLoanFromStore],
  );
  const { hasShortfall, loanAmount, remainingBalance } =
    resolveReservationAmounts(unit, resolvedIskanLoan);

  const unitTitle = `${unit.unitNumber}, ${unit.projectName}`;
  /** Cap to viewport — hug content under the Figma frame / viewport ceiling. */
  const dialogMaxWidth = isMobile ? 420 : 593;
  const frameHeight = hasShortfall ? 854 : 697;
  const viewportCap = Math.max(320, windowHeight - VIEWPORT_VERTICAL_INSET);
  const dialogMaxHeight = Math.min(frameHeight, viewportCap);

  const proceedLabel = labels.CONFIRM_PROCEED;

  const scrollBody = (
    <ConfirmReservationScrollBody
      unit={unit}
      unitTitle={unitTitle}
      imageSource={imageSource}
      isMobile={isMobile}
      colors={colors}
      labels={labels}
      loanAmount={loanAmount}
      hasShortfall={hasShortfall}
      remainingBalance={remainingBalance}
    />
  );
  if (isMobile) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={handleDismiss}
      >
        <View
          style={[
            styles.mobileRoot,
            {
              backgroundColor: colors.backgroundLevel2,
              paddingTop: insets.top,
              paddingBottom: Math.max(insets.bottom, 16),
            },
          ]}
        >
          <View style={styles.mobileHeader}>
            <Pressable
              onPress={handleDismiss}
              style={styles.mobileBackBtn}
              accessibilityRole="button"
              accessibilityLabel={labels.CLOSE}
            >
              <BackArrowIcon size={24} color={colors.fontPrimary} />
            </Pressable>
            <Typography
              variant="title3"
              color="primary"
              style={styles.mobileHeaderTitle}
            >
              {labels.RESERVE_UNIT}
            </Typography>
            <View style={styles.mobileHeaderSpacer} />
          </View>

          <ScrollView
            style={styles.mobileScroll}
            contentContainerStyle={styles.mobileScrollContent}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            <View style={styles.mobileIntro}>
              <Typography
                variant="headlineLarge"
                color="primary"
                style={styles.mobileConfirmTitle}
              >
                {labels.CONFIRM_RESERVATION_TITLE}
              </Typography>
              <Typography
                variant="subhead"
                color={colors.fontSecondary}
                style={styles.mobileSubtitle}
              >
                {labels.CONFIRM_RESERVATION_SUBTITLE}
              </Typography>
            </View>
            <View style={styles.mobileBodyStack}>{scrollBody}</View>
          </ScrollView>

          <View style={styles.mobileFooter}>
            <Button
              title={proceedLabel}
              variant="primary"
              fullWidth
              onPress={handleProceed}
              style={styles.mobileProceedBtn}
              textStyle={styles.mobileProceedLabel}
            />
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <ContentDialog
      visible={visible}
      onClose={handleDismiss}
      contentAlign="left"
      maxWidth={dialogMaxWidth}
      title={labels.CONFIRM_RESERVATION_TITLE}
      titleVariant="title2"
      titleColor="primary"
      titleStyle={styles.titleDesktop}
      subtitle={labels.CONFIRM_RESERVATION_SUBTITLE}
      subtitleVariant="body"
      subtitleColor="secondary"
      subtitleStyle={styles.subtitle}
      headerStyle={styles.headerDesktop}
      cardStyle={[
        styles.cardDesktop,
        {
          backgroundColor: colors.backgroundLevel2,
          maxHeight: dialogMaxHeight,
          paddingTop: 32,
          paddingRight: 32,
          paddingBottom: 32,
          paddingLeft: 32,
          ...(Platform.OS === 'web'
            ? ({ boxSizing: 'border-box' } as const)
            : {}),
        },
      ]}
      closeOnBackdropPress
      actions={[
        {
          label: hasShortfall
            ? labels.CONFIRM_CANCEL_SHORTFALL
            : labels.CONFIRM_CANCEL,
          variant: 'outline',
          onPress: handleDismiss,
        },
        {
          label: proceedLabel,
          variant: 'primary',
          onPress: handleProceed,
        },
      ]}
    >
      <ScrollView
        style={styles.scrollDesktop}
        contentContainerStyle={styles.scrollContentDesktop}
        showsVerticalScrollIndicator={false}
        bounces={false}
        nestedScrollEnabled
      >
        {scrollBody}
      </ScrollView>
    </ContentDialog>
  );
}

function SpecItem({
  icon,
  value,
  label,
  compact = false,
} : Readonly<{
  icon: React.ReactNode;
  value: string;
  label: string;
  compact?: boolean;
}>) {
  return (
    <View style={compact ? styles.specItemMobile : styles.specItem}>
      <View style={styles.specHeader}>
        {icon}
        <Typography
          variant="headline"
          color="primary"
          style={styles.specValue}
          numberOfLines={1}
        >
          {value}
        </Typography>
      </View>
      <Typography
        variant="caption"
        color="secondary"
        style={compact ? styles.specLabelMobile : styles.specLabel}
      >
        {label}
      </Typography>
    </View>
  );
}

function BreakdownRow({
  label,
  amount,
  color,
  leading,
  hug = false,
}: Readonly<{
  label: string;
  amount: number;
  color: string;
  leading?: React.ReactNode;
  hug?: boolean;
}>) {
  return (
    <View style={hug ? styles.breakdownRowHug : styles.breakdownRow}>
      <View style={styles.breakdownLabel}>
        {leading}
        <Typography
          variant="body"
          color={color}
          style={[styles.breakdownLabelText, styles.sizeS]}
        >
          {label}
        </Typography>
      </View>
      <View style={styles.amountRow}>
        <DirhamIcon size={14} color={color} />
        <Typography variant="body" color={color} style={styles.sizeS}>
          {amount.toLocaleString()}
        </Typography>
      </View>
    </View>
  );
}

function DashedDivider({ color }: Readonly<{ color: string }>) {
  return (
    <View
      style={[styles.dashedLine, { borderBottomColor: color }]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    />
  );
}

const styles = StyleSheet.create({
  /**
   * Desktop dialog frame (Figma):
   * Case 1: 593×697 · Case 2: height overridden to 854 via cardStyle.
   * Shared: gap 32, radius 16, padding Spacing/8 (32).
   */
  cardDesktop: {
    width: 593,
    borderRadius: 16,
    paddingTop: 32,
    paddingRight: 32,
    paddingBottom: 32,
    paddingLeft: 32,
    gap: 32,
    overflow: 'hidden',
  },
  cardMobile: {
    borderRadius: 16,
    padding: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 24,
    overflow: 'hidden',
  },
  /** Mobile full-screen confirm (Figma Reserve Unit) */
  mobileRoot: {
    flex: 1,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    minHeight: 48,
  },
  mobileBackBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileHeaderTitle: {
    flex: 1,
    textAlign: 'center',
    /** Size/L Medium, LH 100% */
    lineHeight: 20,
  },
  mobileHeaderSpacer: {
    width: 44,
  },
  mobileIntro: {
    gap: 12,
  },
  /** Confirm your reservation — Size/M SemiBold, LH 100% */
  mobileConfirmTitle: {
    lineHeight: 18,
  },
  /** Review details — Size/S Regular; looser LH for multi-line */
  mobileSubtitle: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 24,
    /** Subtitle ↔ image card */
    gap: 24,
  },
  /** Unit / note / what-next stack */
  mobileBodyStack: {
    width: '100%',
    gap: 16,
  },
  mobileFooter: {
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  mobileProceedBtn: {
    borderRadius: 4,
    height: 48,
  },
  mobileProceedLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
  /** Title ↔ subtitle — small gap in frame */
  headerDesktop: {
    gap: 8,
    flexShrink: 0,
  },
  headerMobile: {
    gap: 8,
    flexShrink: 0,
  },
  /** Confirm title — Font/Size/2XL Medium, LH 100% */
  titleDesktop: {
    lineHeight: 22,
  },
  /** Subtitle — Base Regular, LH 22, #4D4D4D (secondary) */
  subtitle: {
    lineHeight: 22,
  },
  scrollDesktop: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    width: '100%',
  },
  scrollMobile: {
    maxHeight: 420,
    width: '100%',
  },
  /** Unit/breakdown group ↔ note ↔ what-next — 16 */
  scrollContentDesktop: {
    gap: 16,
    paddingBottom: 0,
  },
  scrollContentMobile: {
    gap: 16,
    paddingBottom: 4,
  },
  /** Gap between unit (image) card and Total / Iskan card */
  unitAndBreakdown: {
    gap: 16,
    width: '100%',
    alignItems: 'center',
  },
  /**
   * Unit card — Figma: 529×145, gap 12, radius 12,
   * padding Spacing/2 (8) vertical, 12 horizontal. White bg, no border.
   */
  unitCardDesktop: {
    width: 529,
    height: 145,
    borderRadius: 12,
    paddingTop: 8,
    paddingBottom: 8,
    paddingHorizontal: 12,
    gap: 12,
    alignSelf: 'center',
  },
  /** Mobile unit card — 360×165, gap 16, radius 12, pad 16 / 12 */
  unitCardMobile: {
    width: '100%',
    maxWidth: 360,
    height: 165,
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 16,
    paddingHorizontal: 12,
    gap: 16,
    alignSelf: 'center',
  },
  /** Title top + specs bottom match image edges */
  unitTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minHeight: 0,
  },
  /** Title + specs top-aligned with image top edge */
  unitTopMobile: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minHeight: 0,
  },
  thumb: {
    width: DESKTOP_THUMB_WIDTH,
    height: DESKTOP_THUMB_HEIGHT,
    borderRadius: 4,
    overflow: 'hidden',
    flexShrink: 0,
  },
  thumbMobile: {
    width: MOBILE_THUMB_WIDTH,
    height: MOBILE_THUMB_HEIGHT,
    borderRadius: 4,
    overflow: 'hidden',
    flexShrink: 0,
  },
  thumbImage: {
    width: '100%',
    height: '100%',
  },
  unitMeta: {
    flex: 1,
    minWidth: 0,
    height: DESKTOP_THUMB_HEIGHT,
    justifyContent: 'space-between',
    alignItems: 'stretch',
    /** Optical: caption descenders sit inside the image bottom edge */
    paddingBottom: 3,
  },
  /** Title top-aligned with image; bedroom row bottom-aligned with image */
  unitMetaMobile: {
    flex: 1,
    minWidth: 0,
    height: MOBILE_THUMB_HEIGHT,
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  /** Unit name — Base SemiBold, LH 100% */
  unitTitle: {
    lineHeight: 16,
  },
  specsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
  },
  /** Specs stay on one row: icon+count, then label under each */
  specsRowMobile: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 12,
  },
  specItem: {
    alignItems: 'center',
    gap: 2,
  },
  /** Icon + value + label left-aligned; do not shrink so counts stay one line */
  specItemMobile: {
    alignItems: 'flex-start',
    gap: 2,
    flexShrink: 0,
  },
  specHeader: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 4,
  },
  /** Spec value — Base SemiBold, LH 100%, centered, never wraps */
  specValue: {
    lineHeight: 16,
    textAlign: 'center',
    flexShrink: 0,
  },
  /** Spec label — 3XS Regular, LH 100%, #4D4D4D */
  specLabel: {
    lineHeight: 12,
    textAlign: 'center',
  },
  specLabelMobile: {
    lineHeight: 12,
    textAlign: 'left',
  },
  /** Price bar — grey fill inside white card, no border */
  priceBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    direction: 'ltr',
  },
  /** Price bar amount — Size/M SemiBold, LH 100% */
  priceBarAmount: {
    lineHeight: 18,
  },
  /** Handover — Size/XS Medium, LH 100%, #6E6E6E */
  handoverText: {
    lineHeight: 14,
  },
  /** Breakdown rows — Size/S Regular, LH 100% */
  sizeS: {
    fontSize: 14,
    fontFamily: 'Matter-Regular',
    lineHeight: 14,
  },
  /**
   * Financial Breakdown (Figma):
   * Case 1: 529×114 · Case 2: 529×167.
   * No vertical padding — top/bottom space comes from equal flex
   * bands with content centered. Horizontal pad Spacing/4 (16).
   */
  breakdownCardDesktop: {
    width: 529,
    height: 114,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 0,
    alignSelf: 'center',
  },
  /** Case 2 shortfall — 529×167, 3 equal bands */
  breakdownCardDesktopShortfall: {
    height: 167,
  },
  /** Mobile price card — 360 wide, height follows rows */
  breakdownCardMobile: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 12,
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 16,
    gap: 16,
    alignSelf: 'center',
  },
  /**
   * Equal-height band (shares card height with siblings).
   * Top/bottom inset = centered content within first/last band.
   */
  breakdownRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  breakdownRowHug: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  breakdownLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    minWidth: 0,
  },
  breakdownLabelText: {
    flexShrink: 1,
  },
  dashedLine: {
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
  },
  /**
   * Important to Note — Case 2 only (Figma):
   * 529×80, gap 12, radius Spacing/3 (12), border 1, padding Spacing/3 (12).
   */
  noteBoxDesktop: {
    width: 529,
    height: 80,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    alignSelf: 'center',
  },
  /**
   * Important to Note — mobile: 360 wide, height follows content,
   * gap 12, radius 12, border 1, pad Spacing/3 (12).
   */
  noteBoxMobile: {
    width: '100%',
    maxWidth: 360,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    alignSelf: 'center',
  },
  noteText: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    gap: 6,
  },
  /** Important to Note — Base SemiBold, LH 100%, #E64A19 */
  noteTitle: {
    lineHeight: 16,
  },
  /** Note body — 2XS Regular; looser LH so lines use full width comfortably */
  noteBody: {
    lineHeight: 18,
    width: '100%',
  },
  /**
   * What happens next — Figma: 529×152, gap 16,
   * radius Spacing/3 (12), pad Spacing/4 (16) × Spacing/3 (12),
   * border 1px confirmNextBorder.
   */
  nextBoxDesktop: {
    width: 529,
    height: 152,
    borderWidth: 1,
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 16,
    paddingHorizontal: 12,
    gap: 16,
    alignSelf: 'center',
    overflow: 'hidden',
  },
  /**
   * What happens next — mobile: 360 wide, height follows content,
   * gap 16, radius Spacing/3 (12), pad Spacing/4 (16) × Spacing/3 (12),
   * border 1px.
   */
  nextBoxMobile: {
    width: '100%',
    maxWidth: 360,
    borderWidth: 1,
    borderRadius: 12,
    paddingTop: 16,
    paddingRight: 12,
    paddingBottom: 16,
    paddingLeft: 12,
    gap: 16,
    alignSelf: 'center',
  },
  /** “What happens next?” — Base Regular, LH 22 */
  nextTitle: {
    lineHeight: 22,
  },
  /** Timeline messages — Figma: 503×80, gap 16; icon + text on same row */
  timelineDesktop: {
    width: 503,
    height: 80,
    gap: 16,
    alignSelf: 'center',
    position: 'relative',
  },
  timelineMobile: {
    width: '100%',
    gap: 16,
    position: 'relative',
  },
  timelineStep: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stepIconCircle: {
    width: STEP_ICON_SIZE,
    height: STEP_ICON_SIZE,
    borderRadius: STEP_ICON_SIZE / 2,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  /**
   * Continuous line between icon centers:
   * top = first icon bottom, bottom = second icon top.
   * left centered on STEP_ICON_SIZE (32) rail.
   */
  stepConnector: {
    position: 'absolute',
    left: STEP_ICON_SIZE / 2 - 0.5,
    top: STEP_ICON_SIZE,
    bottom: STEP_ICON_SIZE,
    width: 1,
    zIndex: 0,
  },
  /** Timeline copy — 2XS Regular, #666 (tertiary); looser LH for wrapping lines */
  stepText: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    lineHeight: 20,
  },
});

export default ConfirmReservationDialog;
